import { EmployeeRestaurant } from '@/contexts/user-context';
import { PriceEditorConfig } from '@/domain/pricing/services/price-editor';
import { PriceEditVariantPackDisplaySettings } from '@/types';
import { SalesRepConfig } from '@domain/configuration/types';
import { SalesRepOverrideMode, SalesRepPortionType } from '@/enums';
import { VariantPackInfoType } from '@/contexts/variant-pack-info-context';

/**
 * Acts as an adapter between the Operator app's data layer (variant pack info, restaurant
 * relationship, sales rep config, VP display settings) and the domain-level
 * PriceEditor class.
 *
 * @param variantPackInfo - Variant pack data including price, cost map, and sales rep overrides
 * @param restaurantRelationship - Restaurant relationship with cost type, pricing flags, and PPP setting
 * @param salesRepConfig - Dynamic sales rep config (override mode, floor, display options, etc.)
 * @param variantPackSettings - Per-VP display settings (override editing, PPP, sales rep info toggles)
 *
 * @returns A {@link PriceEditorConfig} ready to be passed to `new PriceEditor(config)`
 *
 * @example
 * ```ts
 * const config = transformToPriceEditorConfig(
 *   variantPackInfo,
 *   selectedRestaurantRelationship,
 *   salesRepConfig,
 *   variantPackSettings,
 * );
 * const editor = new PriceEditor(config);
 * ```
 */
export function transformToPriceEditorConfig(
  variantPackInfo?: VariantPackInfoType | null,
  restaurantRelationship?: EmployeeRestaurant | null,
  salesRepConfig?: SalesRepConfig | null,
  variantPackSettings?: PriceEditVariantPackDisplaySettings | null,
): PriceEditorConfig {
  const costMap = variantPackInfo?.salesRepInfo?.costMap || {};

  const grossProfitDisplayConfig =
    salesRepConfig?.display_options?.configs?.find(
      (config) => config.type === 'gross_profit',
    ) ?? null;

  return {
    costMap,
    currencyCode: variantPackInfo?.currencyCode,
    // Temporary expiries are an order-entry-table-only flow.
    currentExpirationDate: null,
    currentGrossProfitOverridePercent:
      variantPackInfo?.salesRepInfo?.salesRepGrossProfitOverrideInPercentage ??
      null,
    currentPriceOverrideInMicros:
      variantPackInfo?.salesRepInfo?.salesRepPriceInMicros ?? null,
    customerCostType: restaurantRelationship?.customerCostType ?? null,
    defaultUnitPriceInMicros: null,
    desiredPortionType:
      (salesRepConfig?.desired_portion_type as SalesRepPortionType) ?? null,
    disableOverrideEditing: !!(
      salesRepConfig?.disable_override_editing ||
      variantPackSettings?.disable_override_editing
    ),
    disableSalesRepInfo: variantPackSettings?.disable_sales_rep_info ?? false,
    grossProfitDisplayConfig,
    onHandQuantity:
      variantPackInfo?.salesRepInfo?.inventory?.onHandQuantity ?? null,
    overrideMode:
      (salesRepConfig?.override_mode as SalesRepOverrideMode) ??
      SalesRepOverrideMode.DISABLED,
    peakPriceProtection:
      variantPackSettings?.enable_peak_price_protection ??
      restaurantRelationship?.salesRepPeakPriceProtection ??
      false,
    priceCeilingInMicros:
      variantPackInfo?.salesRepInfo?.ceilingPriceInMicros ?? null,
    priceFloorEnabled: salesRepConfig?.price_floor_enabled ?? false,
    priceInMicros: variantPackInfo?.priceInMicros ?? null,
    salesRepGrossProfitEnabled:
      restaurantRelationship?.salesRepGrossProfitEnabled ?? false,
    salesRepPriceFloorMode:
      restaurantRelationship?.salesRepPriceFloorMode ?? null,
    salesRepPricingEnabled:
      restaurantRelationship?.salesRepPricingEnabled ?? false,
    unitCount: variantPackInfo?.unitCount,
  };
}
